"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { View, StyleSheet, Platform, TouchableOpacity, ActivityIndicator, Image } from "react-native"
import { Text, useTheme, Button, Surface, Menu, IconButton } from "react-native-paper"
import { useTheme as useAppTheme } from "../contexts/ThemeContext"
import { useRoute, useNavigation } from "@react-navigation/native"
import { TreeView } from "../components/TreeView"
import { ResizablePanel } from "../components/ResizablePanel"
import { PANEL_CONSTANTS } from "../constants/panelConstants"
import { DrawingsTab } from "../components/TabComponents/DrawingsTab"
import { LineMapTab } from "../components/TabComponents/LineMapTab"
import { ModelTab } from "../components/TabComponents/ModelTab"
import { NodeSummaryTab } from "../components/TabComponents/NodeSummaryTab"
import { LocationsTab } from "../components/ProjectDetails/tabs/LocationsTab"
import { SpansTab } from "../components/ProjectDetails/tabs/SpansTab"
import { SectionsTab } from "../components/ProjectDetails/tabs/SectionsTab"
import { TowerWeightsTab } from "../components/ProjectDetails/tabs/TowerWeightsTab"
import { FoundationQuantityTab } from "../components/ProjectDetails/tabs/FoundationQuantityTab"
import { FoundationQuantityByTowerTypeTab } from "../components/ProjectDetails/tabs/FoundationQuantityByTowerTypeTab"
import { UnitsOfMeasure } from "../components/UnitsOfMeasure"
import type { ProjectData } from "../types/project"
import type { TreeNode } from "../components/TreeView/types"
import type { TabComponentProps } from "../types/tabs"
import { useAuth } from "../contexts/AuthContext"
import { DashboardsTab } from "../components/TabComponents/DashBoards/DashboardsTab"
import { WeatherDetailsTab } from "../components/TabComponents/Weather/WeatherDetailsTab"
import { GalleryTab } from "../components/ProjectDetails/tabs/GalleryTab"
import { ErectionTab } from "../components/TabComponents/Reports/ErectionTab"
import { StringingTab } from "../components/TabComponents/Reports/StringingTab"
import { SummaryTab } from "../components/TabComponents/Reports/SummaryTab"
import { OverviewTab } from "../components/TabComponents/Reports/OverviewTab"
import projectService from "../services/projectService"
import logoImage from "../../assets/logo.png"
import type { AssetNode } from "../types/lineMap"

interface TabGroup {
  id: string
  title: string
  tabs: {
    id: string
    title: string
    component: React.ComponentType<TabComponentProps>
  }[]
}

const TAB_GROUPS: TabGroup[] = [
  {
    id: "dashboards",
    title: "DashBoard",
    tabs: [
      { id: "lineMap", title: "Line Map", component: LineMapTab },
      { id: "details", title: "Details", component: DashboardsTab },
    ],
  },
  {
    id: "overview",
    title: "OverView",
    tabs: [
      { id: "nodeSummary", title: "Summary", component: NodeSummaryTab },
      { id: "drawings", title: "Drawings", component: DrawingsTab },
      { id: "3dModel", title: "3D Model", component: ModelTab },
    ],
  },
  {
    id: "details",
    title: "Details",
    tabs: [
      { id: "locations", title: "Towers", component: LocationsTab },
      { id: "spans", title: "Spans", component: SpansTab },
      { id: "towerWeights", title: "Tower Weights", component: TowerWeightsTab },
      {
        id: "foundationQuantityBySoilType",
        title: "Foundation Quantity By Soil Type",
        component: FoundationQuantityTab,
      },
      {
        id: "foundationQuantityByTowerType",
        title: "Foundation Quantity By Tower Type",
        component: FoundationQuantityByTowerTypeTab,
      },
      { id: "sections", title: "Sections", component: SectionsTab },
      { id: "gallery", title: "Gallery", component: GalleryTab },
    ],
  },
  {
    id: "weather",
    title: "Weather",
    tabs: [{ id: "overview", title: "Overview", component: WeatherDetailsTab }],
  },
  {
    id: "reports",
    title: "Reports",
    tabs: [
      { id: "erection", title: "Erection", component: ErectionTab },
      { id: "stringing", title: "Stringing", component: StringingTab },
      { id: "summary", title: "Summary", component: SummaryTab },
      { id: "overview", title: "Overview", component: OverviewTab },
    ],
  },
]

const ProjectViewNested: React.FC = () => {
  const theme = useTheme()
  const { toggleTheme, isDarkMode } = useAppTheme()
  const { logout, apiEndpoint } = useAuth()
  const navigation = useNavigation()
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null)
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [projectData, setProjectData] = useState<ProjectData | null>(null)
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assetName, setAssetName] = useState<string | null>(null)
  // Add a state to track the selected node from the map
  const [mapSelectedNode, setMapSelectedNode] = useState<AssetNode | null>(null)

  const route = useRoute<any>()
  const projectId = route.params?.projectId
  const projectName = route.params?.projectName || projectId

  const [activeParentTab, setActiveParentTab] = useState(TAB_GROUPS[0].id)
  const [activeChildTabs, setActiveChildTabs] = useState<Record<string, string>>({
    [TAB_GROUPS[0].id]: TAB_GROUPS[0].tabs[0].id,
    [TAB_GROUPS[1].id]: TAB_GROUPS[1].tabs[0].id,
    [TAB_GROUPS[2].id]: TAB_GROUPS[2].tabs[0].id,
    [TAB_GROUPS[3].id]: TAB_GROUPS[3].tabs[0].id,
    [TAB_GROUPS[4].id]: TAB_GROUPS[4].tabs[0].id,
  })

  const [menuState, setMenuState] = useState<{
    activeMenu: "file" | "view" | "tools" | "application" | "maintenance" | null
    activeMenuItem:
    | "edit"
    | "delete"
    | "columnOptions"
    | "expandTable"
    | "validateTowerSchedule"
    | "maximumPumpingPower"
    | "manualRecords"
    | "droneData"
    | null
  }>({
    activeMenu: null,
    activeMenuItem: null,
  })

  const [unitsModalVisible, setUnitsModalVisible] = useState(false)

  useEffect(() => {
    const loadProjectData = async () => {
      if (!projectId || !apiEndpoint) return

      try {
        // Loading project...

        setLoading(true)
        setError(null)

        // First get the project details from the API
        // Calling projectService.getProjectById
        const project = await projectService.getProjectById(projectId, apiEndpoint)
        // getProjectById result

        if (!project) {
          // Project not found
          setError("Project not found")
          setLoading(false)
          return
        }

        // Project found

        // Store the asset name from the project
        setAssetName(projectName || project.name)
        // Setting asset name

        // Get the list of files for this project
        const response = await fetch(project.url)

        if (!response.ok) {
          throw new Error(`Failed to fetch project contents: ${response.statusText}`)
        }

        // Check if the response is JSON
        const contentType = response.headers.get("content-type")
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error(`Project URL returned non-JSON response (${contentType}). The URL might be incorrect or the server is returning an HTML page.`)
        }

        const directoryData = await response.json()

        // Find the Info.json file in the files list
        const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

        if (!infoFile) {
          throw new Error("Info.json file not found in project")
        }

        // Fetch the Info.json file content
        const infoResponse = await fetch(infoFile.url)

        if (!infoResponse.ok) {
          throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
        }

        // Check if Info.json response is JSON
        const infoContentType = infoResponse.headers.get("content-type")
        if (!infoContentType || !infoContentType.includes("application/json")) {
          throw new Error(`Info.json returned non-JSON response. The file might not be accessible or the URL is incorrect.`)
        }

        const infoData = await infoResponse.json()

        if (!infoData || !infoData.Lines) {
          throw new Error("Invalid project data format - missing Lines property")
        }

        setProjectData(infoData)
      } catch (error: any) {
        // Error loading project data
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }

    loadProjectData()
  }, [projectId, projectName, apiEndpoint])

  const handleNodeSelect = (node: TreeNode) => {
    setSelectedNode(node)
    setSelectedNodes([node.name])
  }

  const handleExport = () => {
    // Export
    setMenuState({ activeMenu: null, activeMenuItem: null })
  }

  const handleColumnOptions = () => {
    setMenuState({ activeMenu: null, activeMenuItem: "columnOptions" })
  }

  const handleExpandTable = () => {
    setMenuState({ activeMenu: null, activeMenuItem: "expandTable" })
  }

  const handleViewMenuDismiss = () => {
    setMenuState({ activeMenu: null, activeMenuItem: null })
  }

  const activeGroup = TAB_GROUPS.find((group) => group.id === activeParentTab)
  const activeTab = activeGroup?.tabs.find((tab) => tab.id === activeChildTabs[activeParentTab])

  const handleParentTabClick = (groupId: string) => {
    setActiveParentTab(groupId)
    setMenuState({ activeMenu: null, activeMenuItem: null })
  }

  const handleChildTabClick = (groupId: string, tabId: string) => {
    setActiveChildTabs((prev) => ({ ...prev, [groupId]: tabId }))
    setMenuState({ activeMenu: null, activeMenuItem: null })
  }

  useEffect(() => {
    if (menuState.activeMenuItem) {
      const timer = setTimeout(() => {
        setMenuState({ activeMenu: null, activeMenuItem: null })
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [menuState.activeMenuItem])

  if (loading) {
    return (
      <View
        style={[
          styles.container,
          { backgroundColor: theme.colors.background, justifyContent: "center", alignItems: "center" },
        ]}
      >
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 16, color: theme.colors.onSurface }}>Loading project data...</Text>
      </View>
    )
  }

  if (error) {
    return (
      <View
        style={[
          styles.container,
          { backgroundColor: theme.colors.background, justifyContent: "center", alignItems: "center" },
        ]}
      >
        <Text style={{ color: theme.colors.error, textAlign: "center", marginBottom: 16 }}>Error: {error}</Text>
        <Button mode="contained" onPress={() => navigation.goBack()} style={{ marginTop: 16 }}>
          Go Back
        </Button>
      </View>
    )
  }

  if (!projectData) {
    return (
      <View
        style={[
          styles.container,
          { backgroundColor: theme.colors.background, justifyContent: "center", alignItems: "center" },
        ]}
      >
        <Text style={{ color: theme.colors.onSurface }}>No project data available</Text>
        <Button mode="contained" onPress={() => navigation.goBack()} style={{ marginTop: 16 }}>
          Go Back
        </Button>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      {/* Custom header with menu bar */}
      <Surface style={[styles.header, { backgroundColor: theme.colors.elevation.level2 }]} elevation={4}>
        <View style={styles.headerContent}>
          <View style={styles.leftSection}>
            <Image source={logoImage} style={styles.logo} resizeMode="contain" />
            <View style={styles.menuSection}>
              <Menu
                visible={menuState.activeMenu === "file"}
                onDismiss={() => setMenuState({ activeMenu: null, activeMenuItem: null })}
                anchor={
                  <TouchableOpacity
                    onPress={() => setMenuState({ activeMenu: "file", activeMenuItem: null })}
                    style={[
                      styles.menuButton,
                      menuState.activeMenu === "file" && { backgroundColor: theme.colors.elevation.level3 },
                    ]}
                  >
                    <Text style={[styles.menuText, { color: theme.colors.onSurface }]}>File</Text>
                  </TouchableOpacity>
                }
                anchorPosition="bottom"
                contentStyle={[styles.menuContent, { backgroundColor: theme.colors.elevation.level3 }]}
              >
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: "edit" })
                    // Edit
                  }}
                  title="Edit"
                  style={styles.menuItem}
                  leadingIcon="pencil"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: "delete" })
                    // Delete
                  }}
                  title="Delete"
                  style={styles.menuItem}
                  leadingIcon="delete"
                  titleStyle={{ fontSize: 11, color: theme.colors.error }}
                />
              </Menu>

              <Menu
                visible={menuState.activeMenu === "view"}
                onDismiss={() => setMenuState({ activeMenu: null, activeMenuItem: null })}
                anchor={
                  <TouchableOpacity
                    onPress={() => setMenuState({ activeMenu: "view", activeMenuItem: null })}
                    style={[
                      styles.menuButton,
                      menuState.activeMenu === "view" && { backgroundColor: theme.colors.elevation.level3 },
                    ]}
                  >
                    <Text style={[styles.menuText, { color: theme.colors.onSurface }]}>View</Text>
                  </TouchableOpacity>
                }
                anchorPosition="bottom"
                contentStyle={[styles.menuContent, { backgroundColor: theme.colors.elevation.level3 }]}
              >
                <Menu.Item
                  onPress={handleColumnOptions}
                  title="Column Options"
                  style={styles.menuItem}
                  leadingIcon="table-column"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
                <Menu.Item
                  onPress={handleExpandTable}
                  title="Expand Table"
                  style={styles.menuItem}
                  leadingIcon="arrow-expand"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
              </Menu>

              <Menu
                visible={menuState.activeMenu === "tools"}
                onDismiss={() => setMenuState({ activeMenu: null, activeMenuItem: null })}
                anchor={
                  <TouchableOpacity
                    onPress={() => setMenuState({ activeMenu: "tools", activeMenuItem: null })}
                    style={[
                      styles.menuButton,
                      menuState.activeMenu === "tools" && { backgroundColor: theme.colors.elevation.level3 },
                    ]}
                  >
                    <Text style={[styles.menuText, { color: theme.colors.onSurface }]}>Tools</Text>
                  </TouchableOpacity>
                }
                anchorPosition="bottom"
                contentStyle={[styles.menuContent, { backgroundColor: theme.colors.elevation.level3 }]}
              >
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: null })
                    setUnitsModalVisible(true)
                  }}
                  title="Units of Measure"
                  style={styles.menuItem}
                  leadingIcon="ruler"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
              </Menu>

              <Menu
                visible={menuState.activeMenu === "application"}
                onDismiss={() => setMenuState({ activeMenu: null, activeMenuItem: null })}
                anchor={
                  <TouchableOpacity
                    onPress={() => setMenuState({ activeMenu: "application", activeMenuItem: null })}
                    style={[
                      styles.menuButton,
                      menuState.activeMenu === "application" && { backgroundColor: theme.colors.elevation.level3 },
                    ]}
                  >
                    <Text style={[styles.menuText, { color: theme.colors.onSurface }]}>Apps</Text>
                  </TouchableOpacity>
                }
                anchorPosition="bottom"
                contentStyle={[styles.menuContent, { backgroundColor: theme.colors.elevation.level3 }]}
              >
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: "validateTowerSchedule" })
                    // Validate Tower Schedule
                  }}
                  title="Validate Tower Schedule"
                  style={styles.menuItem}
                  leadingIcon="check-circle"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: "maximumPumpingPower" })
                    // MaxPumping Power
                  }}
                  title="MaxPumping Power"
                  style={styles.menuItem}
                  leadingIcon="lightning-bolt"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
              </Menu>

              <Menu
                visible={menuState.activeMenu === "maintenance"}
                onDismiss={() => setMenuState({ activeMenu: null, activeMenuItem: null })}
                anchor={
                  <TouchableOpacity
                    onPress={() => setMenuState({ activeMenu: "maintenance", activeMenuItem: null })}
                    style={[
                      styles.menuButton,
                      menuState.activeMenu === "maintenance" && { backgroundColor: theme.colors.elevation.level3 },
                    ]}
                  >
                    <Text style={[styles.menuText, { color: theme.colors.onSurface }]}>Maintenance</Text>
                  </TouchableOpacity>
                }
                anchorPosition="bottom"
                contentStyle={[styles.menuContent, { backgroundColor: theme.colors.elevation.level3 }]}
              >
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: "manualRecords" })
                    // Manual Records
                  }}
                  title="Manual Records"
                  style={styles.menuItem}
                  leadingIcon="clipboard-text"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
                <Menu.Item
                  onPress={() => {
                    setMenuState({ activeMenu: null, activeMenuItem: "droneData" })
                    // Drone Data
                  }}
                  title="Drone Data"
                  style={styles.menuItem}
                  leadingIcon="drone"
                  titleStyle={{ fontSize: 11, color: theme.colors.onSurface }}
                />
              </Menu>
            </View>
          </View>

          <View style={styles.rightSection}>
            <IconButton
              icon="home"
              size={24}
              onPress={() => navigation.navigate("AllProjectsDashboard" as never)}
              iconColor={theme.colors.primary}
            />
            <IconButton
              icon={isDarkMode ? "weather-sunny" : "weather-night"}
              size={24}
              onPress={toggleTheme}
              iconColor={theme.colors.primary}
            />
            <IconButton icon="logout" size={24} onPress={logout} iconColor={theme.colors.error} />
          </View>
        </View>
      </Surface>

      <View style={[styles.content, { backgroundColor: theme.colors.elevation.level1 }]}>
        <ResizablePanel
          position="left"
          defaultWidth={PANEL_CONSTANTS.LEFT_PANEL.DEFAULT_WIDTH}
          minWidth={PANEL_CONSTANTS.LEFT_PANEL.MIN_WIDTH}
          maxWidth={PANEL_CONSTANTS.LEFT_PANEL.MAX_WIDTH}
          onCollapse={setLeftPanelCollapsed}
        >
          <View style={styles.treeContainer}>
            {projectData?.Lines && (
              <TreeView
                data={projectData.Lines}
                onSelect={handleNodeSelect}
                selectedNodes={selectedNodes}
                externalSelectedNode={mapSelectedNode}
              />
            )}
          </View>
        </ResizablePanel>

        <View style={styles.mainContent}>
          <View style={styles.parentTabsContainer}>
            {TAB_GROUPS.map((group) => (
              <TouchableOpacity
                key={group.id}
                style={[styles.parentTab, activeParentTab === group.id && { borderBottomColor: theme.colors.primary }]}
                onPress={() => handleParentTabClick(group.id)}
              >
                <Text
                  style={[
                    styles.parentTabText,
                    { color: activeParentTab === group.id ? theme.colors.primary : theme.colors.onSurface },
                  ]}
                >
                  {group.title}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.childTabsContainer}>
            {activeGroup?.tabs.map((tab) => (
              <TouchableOpacity
                key={tab.id}
                style={[
                  styles.childTab,
                  activeChildTabs[activeParentTab] === tab.id && { borderBottomColor: theme.colors.primary },
                ]}
                onPress={() => handleChildTabClick(activeParentTab, tab.id)}
              >
                <Text
                  style={[
                    styles.childTabText,
                    {
                      color:
                        activeChildTabs[activeParentTab] === tab.id ? theme.colors.primary : theme.colors.onSurface,
                    },
                  ]}
                >
                  {tab.title}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.tabContent}>
            {activeTab && (
              <activeTab.component
                selectedNode={selectedNode}
                projectData={projectData}
                assetName={assetName}
                menuAction={
                  menuState.activeMenuItem
                    ? {
                      type: menuState.activeMenuItem === "columnOptions" ? "columnOptions" : "expandTable",
                      visible: true,
                    }
                    : undefined
                }
                {...(activeTab.id === "lineMap" ? { onNodeSelect: setMapSelectedNode } : {})}
              />
            )}
          </View>
        </View>
      </View>
      <UnitsOfMeasure visible={unitsModalVisible} onDismiss={() => setUnitsModalVisible(false)} />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: "100vh",
    width: "100%",
  },
  header: {
    height: Platform.OS === "web" ? 45 : 56,
    justifyContent: "center",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  content: {
    flex: 1,
    flexDirection: "row",
    overflow: "hidden",
    height: "calc(100vh - 56px)",
  },
  treeContainer: {
    flex: 1,
    padding: 8,
    height: "100%",
    overflow: "auto",
  },
  mainContent: {
    flex: 1,
    height: "100%",
    overflow: "hidden",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  tabContent: {
    flex: 1,
    overflow: "auto",
  },
  menuSection: {
    flexDirection: "row",
    marginLeft: 16,
    alignItems: "center",
    height: "100%",
  },
  menuButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginHorizontal: 2,
    borderRadius: 4,
    height: 28,
    justifyContent: "center",
  },
  menuText: {
    fontSize: 14,
    fontWeight: "500",
  },
  menuContent: {
    borderRadius: 4,
    marginTop: 2,
    minWidth: 30,
    width: 180, // Adjust width to accommodate longer text
    padding: 0,
    zIndex: 1000,
  },
  menuItem: {
    height: 28,
    borderRadius: 4,
    paddingVertical: 0,
    paddingLeft: 2,
    paddingRight: 2,
    minWidth: 180, // Add minimum width
  },
  logo: {
    height: 40,
    width: 120,
    marginRight: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
  },
  parentTabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    height: 40,
  },
  parentTab: {
    paddingHorizontal: 12,
    height: "100%",
    justifyContent: "center",
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  parentTabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  childTabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    height: 32,
    paddingLeft: 16,
  },
  childTab: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    height: "100%",
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  childTabText: {
    fontSize: 13,
    marginRight: 8,
  },
})

export default ProjectViewNested

